<template>
  <section :class="modalOnly ? 'port-card-modal-only' : 'vp-card'" data-profile-card="ports">
    <div
      v-if="!modalOnly"
      class="vp-card-head"
      :class="{ 'pv-accordion-head': collapsible }"
      :role="collapsible ? 'button' : null"
      :tabindex="collapsible ? 0 : null"
      :aria-expanded="collapsible ? isExpanded : null"
      @click="toggleCard"
      @keydown.enter.self.prevent="toggleCard"
      @keydown.space.self.prevent="toggleCard"
    >
      <h6 class="vp-card-title">
        <i class="ph-duotone ph-anchor"></i>
        {{ t('profile.sections.ports') }}
        <span v-if="totalItems" class="vp-chip">{{ $formatInteger(totalItems) }}</span>
      </h6>
      <div class="d-flex align-items-center gap-2">
        <button
          v-if="canAssignPortToTenant"
          type="button"
          class="vp-btn vp-btn-ghost vp-btn-sm"
          @click.stop="openPortModal">
          <i class="ph-duotone ph-plus"></i>
          {{ t('profile.ports.addPort') }}
        </button>
        <i v-if="collapsible" class="ph-duotone pv-accordion-caret" :class="isExpanded ? 'ph-caret-up' : 'ph-caret-down'"></i>
      </div>
    </div>

    <div v-show="isExpanded && !modalOnly" class="vp-card-body position-relative">
      <!-- Loading state -->
      <div v-if="isLoading && !loadingMore" class="vp-loading">
        <span class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">{{ t('common.loading') }}</span>
        </span>
      </div>

      <!-- Error message -->
      <div v-else-if="error" class="vp-note vp-note--danger" role="alert">
        {{ error }}
      </div>

      <!-- Empty state -->
      <div v-else-if="!ports.length" class="vp-empty">
        <i class="ph-duotone ph-map-pin-line vp-empty-icon"></i>
        <div class="vp-empty-text mb-3">{{ t('profile.ports.noPorts') }}</div>
        <button
          v-if="canAssignPortToTenant"
          type="button"
          class="vp-btn vp-btn-primary vp-btn-sm"
          @click.stop="openPortModal">
          <i class="ph-duotone ph-plus"></i>
          {{ t('profile.ports.addFirstPort') }}
        </button>
      </div>

      <template v-else>
        <!-- Filter: only worth showing once the list is long enough to scan -->
        <div v-if="ports.length > CARD_FILTER_THRESHOLD" class="port-card-filter">
          <i class="ph-duotone ph-magnifying-glass port-card-filter-icon"></i>
          <input
            type="text"
            v-model="cardFilter"
            :aria-label="t('profile.ports.filterPorts')"
            :placeholder="t('profile.ports.filterPorts')">
          <button
            v-if="cardFilter"
            type="button"
            class="port-card-filter-clear"
            :aria-label="t('common.clear')"
            @click="cardFilter = ''">
            <i class="ph-duotone ph-x"></i>
          </button>
        </div>

        <div v-if="!groupedCardPorts.length" class="vp-empty py-3">
          <div class="vp-empty-text mt-0">{{ t('common.noResults') }}</div>
        </div>

        <!--
          Countries first, ports on demand: a flat list of every port ran the card down
          the page. A filter opens every matching country so the hits are visible.
        -->
        <div v-if="groupedCardPorts.length" class="port-card-countries">
        <div
          v-for="group in groupedCardPorts"
          :key="group.countryName"
          class="port-card-group"
          :class="{ 'is-open': isCardCountryOpen(group.countryName) }"
        >
          <button
            type="button"
            class="port-card-group-header"
            :aria-expanded="isCardCountryOpen(group.countryName)"
            @click="toggleCardCountry(group.countryName)"
          >
            <i class="ph-duotone ph-caret-right port-card-group-caret"></i>
            <i class="ph-duotone ph-globe-hemisphere-west port-card-group-icon"></i>
            <span class="port-card-group-name">{{ group.countryName }}</span>
            <span class="port-card-group-count">{{ $formatInteger(group.ports.length) }}</span>
          </button>
          <div v-if="isCardCountryOpen(group.countryName)" class="port-card-group-body">
          <div
            v-for="port in group.ports"
            :key="port.id"
            class="port-card-row">
            <i class="ph-duotone ph-map-pin port-card-row-icon"></i>
            <span class="port-card-row-name">{{ port.portName || port.name }}</span>
            <span v-if="port.portCode || port.code" class="port-card-row-code">
              {{ port.portCode || port.code }}
            </span>
            <button
              v-if="canDeletePorts"
              type="button"
              class="port-card-row-delete"
              :disabled="deletingId === port.id"
              :title="t('common.actions.delete')"
              @click.prevent="deletePort(port)">
              <span v-if="deletingId === port.id" class="spinner-border spinner-border-sm"></span>
              <i v-else class="ph-duotone ph-trash"></i>
            </button>
          </div>
          </div>
        </div>
        </div>

        <!-- Load more -->
        <div v-if="hasMorePages && !cardFilter" class="text-center pt-3">
          <button
            type="button"
            class="vp-btn vp-btn-ghost vp-btn-sm"
            @click="loadMorePorts"
            :disabled="loadingMore">
            <span v-if="loadingMore" class="spinner-border spinner-border-sm"></span>
            {{ loadingMore ? t('common.loading') : t('common.showMore') }}
          </button>
        </div>
      </template>
    </div>
    
    <!-- Port Selection Modal -->
    <div class="modal fade" id="portSelectionModal" tabindex="-1" aria-labelledby="portSelectionModalLabel" aria-hidden="true">
      <div class="modal-dialog port-modal-dialog">
        <div class="modal-content port-modal-content">
          <!-- Header -->
          <div class="port-modal-header">
            <div class="d-flex align-items-baseline gap-2">
              <span class="port-modal-title" id="portSelectionModalLabel">{{ t('profile.ports.selectPorts') }}</span>
              <span class="port-modal-subtitle">
                {{ grandTotalPorts }} {{ t('profile.ports.portsAvailable') }} / {{ countryOptions.length }} {{ t('profile.ports.countries') }}
              </span>
            </div>
            <button type="button" class="port-icon-btn" @click="closeModal" :aria-label="t('common.cancel')">✕</button>
          </div>

          <!-- Body: browse (left) + selection panel (right) -->
          <div class="port-modal-body">
            <div class="port-browse">
              <div class="port-browse-toolbar">
                <!-- Search -->
                <div class="port-search">
                  <span class="port-search-icon" aria-hidden="true">⌕</span>
                  <input
                    type="text"
                    class="port-search-input"
                    v-model="searchQuery"
                    :placeholder="t('profile.ports.searchCountryOrPort')"
                    @input="filterCountries">
                  <button
                    v-if="searchQuery"
                    type="button"
                    class="port-search-clear"
                    :aria-label="t('common.clear')"
                    @click="clearSearch">✕</button>
                </div>

                <!-- Segmented filter + expand toggle -->
                <div class="d-flex align-items-center gap-2">
                  <div class="port-segmented" role="group" :aria-label="t('profile.ports.selectPorts')">
                    <button
                      type="button"
                      class="port-segment"
                      :class="{ 'is-active': !showSelectedOnly }"
                      @click="showSelectedOnly = false">
                      {{ t('profile.ports.allCountries') }}
                    </button>
                    <button
                      type="button"
                      class="port-segment"
                      :class="{ 'is-active': showSelectedOnly }"
                      @click="enableShowSelectedOnly">
                      {{ t('profile.ports.selectedOnly') }}
                      <span class="port-segment-badge">{{ selectedPortIds.length }}</span>
                    </button>
                  </div>
                  <div class="flex-grow-1"></div>
                  <button type="button" class="port-link-btn" @click="toggleExpandAll">
                    {{ anyCountryCollapsed ? t('profile.ports.expandAll') : t('profile.ports.collapseAll') }}
                  </button>
                </div>

                <!-- Select all -->
                <div
                  v-if="!isLoadingCountries && !isLoadingAllPorts && visibleCountryOptions.length"
                  class="port-select-all"
                  role="button"
                  tabindex="0"
                  @click="toggleSelectAllCountries"
                  @keydown.enter.prevent="toggleSelectAllCountries"
                  @keydown.space.prevent="toggleSelectAllCountries">
                  <span
                    class="port-cb port-cb-lg"
                    :class="{ 'is-checked': areAllCountriesFullySelected, 'is-partial': isSelectAllPartial }">
                    {{ selectAllGlyph }}
                  </span>
                  <span class="port-select-all-label">{{ selectAllLabel }}</span>
                  <span class="port-select-all-hint">
                    ({{ visibleCountryOptions.length }} {{ t('profile.ports.countries') }})
                  </span>
                  <span v-if="isSelectingAllCountries" class="port-select-all-hint ms-auto d-flex align-items-center">
                    <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {{ t('common.loading') }}
                  </span>
                </div>
              </div>

              <!-- Loading -->
              <div v-if="isLoadingCountries || isLoadingAllPorts" class="port-state-block">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">{{ t('common.loading') }}</span>
                </div>
              </div>

              <!-- Country list -->
              <div v-else class="port-browse-list">
                <div v-if="!visibleCountryOptions.length" class="port-state-block port-state-text">
                  {{ showSelectedOnly ? t('profile.ports.noSelectionHint') : t('common.noResults') }}
                </div>

                <div class="d-flex flex-column gap-1">
                  <div v-for="country in visibleCountryOptions" :key="country.id" class="port-country">
                    <div class="port-country-header">
                      <span
                        class="port-cb port-cb-lg"
                        role="checkbox"
                        tabindex="0"
                        :aria-checked="isCountryFullySelected(country.id) ? 'true' : 'false'"
                        :aria-label="`${t('profile.ports.selectAll')} - ${country.name}`"
                        :class="{
                          'is-checked': isCountryFullySelected(country.id),
                          'is-partial': isCountryPartiallySelected(country.id)
                        }"
                        @click.stop="toggleSelectAllForCountry(country.id)"
                        @keydown.enter.prevent.stop="toggleSelectAllForCountry(country.id)"
                        @keydown.space.prevent.stop="toggleSelectAllForCountry(country.id)">
                        {{ countryCheckboxGlyph(country.id) }}
                      </span>
                      <button
                        type="button"
                        class="port-country-toggle"
                        :aria-expanded="expandedCountries[country.id] ? 'true' : 'false'"
                        :aria-controls="`collapse-${country.id}`"
                        @click="toggleCountryAccordion(country.id)">
                        <span class="port-chevron" :class="{ 'is-open': expandedCountries[country.id] }">▶</span>
                        <span class="port-country-name">{{ country.name }}</span>
                        <span
                          class="port-count-badge"
                          :class="{ 'is-filled': getCountrySelectedCount(country.id) > 0 }">
                          {{ getCountrySelectedCount(country.id) }}
                        </span>
                        <span v-if="countryPortsLoaded[country.id]" class="port-country-total">
                          / {{ getPortsByCountry(country.id).length }} {{ t('profile.ports.portsAvailable') }}
                        </span>
                      </button>
                      <button
                        v-if="getCountrySelectedCount(country.id) > 0"
                        type="button"
                        class="port-clear-btn"
                        :title="t('profile.ports.clearCountry')"
                        @click.stop="clearCountrySelection(country.id)">
                        {{ t('profile.ports.clear') }}
                      </button>
                    </div>

                    <div
                      v-if="expandedCountries[country.id]"
                      :id="`collapse-${country.id}`"
                      class="port-country-body">
                      <div v-if="loadingCountryPorts[country.id]" class="text-center py-2">
                        <div class="spinner-border spinner-border-sm text-primary" role="status">
                          <span class="visually-hidden">{{ t('common.loading') }}</span>
                        </div>
                      </div>

                      <template v-else-if="getFilteredPortsForCountry(country.id).length">
                        <div class="port-grid">
                          <div
                            v-for="port in getFilteredPortsForCountry(country.id)"
                            :key="port.id"
                            class="port-cell"
                            role="checkbox"
                            tabindex="0"
                            :aria-checked="selectedPortIds.includes(String(port.id)) ? 'true' : 'false'"
                            @click="togglePortSelection(port.id)"
                            @keydown.enter.prevent="togglePortSelection(port.id)"
                            @keydown.space.prevent="togglePortSelection(port.id)">
                            <span
                              class="port-cb port-cb-sm"
                              :class="{ 'is-checked': selectedPortIds.includes(String(port.id)) }">
                              {{ selectedPortIds.includes(String(port.id)) ? '✓' : '' }}
                            </span>
                            <span class="port-cell-name">
                              {{ port.portName || port.name }}
                              <span v-if="port.portCode || port.code" class="port-cell-code">({{ port.portCode || port.code }})</span>
                            </span>
                          </div>
                        </div>
                      </template>

                      <div v-else class="port-state-text py-2">
                        {{ t('profile.ports.noPorts') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right: selection panel -->
            <div class="port-panel">
              <div class="port-panel-header">
                <div class="d-flex align-items-center gap-2">
                  <span class="port-panel-title">{{ t('profile.ports.yourSelection') }}</span>
                  <span class="port-panel-count">{{ selectedPortIds.length }}</span>
                </div>
                <button
                  v-if="selectedPortIds.length"
                  type="button"
                  class="port-danger-btn"
                  @click="clearAllSelections">
                  {{ t('profile.ports.clearAll') }}
                </button>
              </div>

              <div v-if="selectedPortIds.length" class="port-panel-summary">
                {{ selectedSummary.length }} {{ t('profile.ports.countries') }} ·
                {{ selectedPortIds.length }} {{ t('profile.ports.portsAvailable') }}
              </div>

              <div class="port-panel-body">
                <div v-if="!selectedSummary.length" class="port-state-block port-state-text">
                  {{ t('profile.ports.noSelectionHint') }}
                </div>

                <div class="d-flex flex-column gap-2">
                  <div v-for="group in selectedSummary" :key="group.countryName" class="port-group">
                    <div
                      class="port-group-header"
                      role="button"
                      tabindex="0"
                      @click="toggleSelectionGroup(group.countryName)"
                      @keydown.enter.prevent="toggleSelectionGroup(group.countryName)"
                      @keydown.space.prevent="toggleSelectionGroup(group.countryName)">
                      <span class="port-chevron port-chevron-sm" :class="{ 'is-open': isSelectionGroupOpen(group) }">▶</span>
                      <span class="port-group-name">{{ group.countryName }}</span>
                      <span class="port-group-badge" :class="{ 'is-filled': group.isFullySelected }">
                        {{ group.isFullySelected
                          ? `${t('profile.ports.allSelectedShort')} ${group.ports.length}`
                          : `${group.ports.length} / ${group.totalPorts}` }}
                      </span>
                      <button
                        type="button"
                        class="port-icon-btn port-icon-btn-sm"
                        :title="t('profile.ports.clearCountry')"
                        @click.stop="removeSelectedCountry(group.countryName)">✕</button>
                    </div>

                    <div v-if="isSelectionGroupOpen(group)" class="port-group-body">
                      <span v-for="port in group.ports" :key="port.id" class="port-chip">
                        {{ port.name }}
                        <button
                          type="button"
                          class="port-chip-remove"
                          :aria-label="`${t('common.actions.delete')} ${port.name}`"
                          @click="removeSelectedPort(port.id)">✕</button>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="port-modal-footer">
            <span class="port-footer-text">
              {{ selectedPortIds.length }} {{ t('profile.ports.portsSelected') }}
            </span>
            <div class="d-flex gap-2">
              <button type="button" class="port-btn port-btn-secondary" @click="closeModal">
                {{ t('common.cancel') }}
              </button>
              <button
                type="button"
                class="port-btn port-btn-primary"
                :disabled="isSaving || isSelectingAllCountries"
                @click="savePorts">
                <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
                {{ t('common.save') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import { useStore } from 'vuex';
import portService from '@/services/portService';
import tenantInfoService from '@/services/tenantInfoService';
import { useI18n } from 'vue-i18n';

export default {
  name: 'ProfilePortCard',
  emits: ['ports-updated'],
  props: {
    /**
     * Whether the head toggles the body and the card joins the accordion. Off where the
     * card stands alone (its own profile tab), where collapsing would just hide the page.
     */
    collapsible: {
      type: Boolean,
      default: true
    },
    /**
     * Renders only the port picker, no card of its own. Used by the Service Ports screen,
     * which draws the list itself and reaches in here purely to open the modal.
     */
    modalOnly: {
      type: Boolean,
      default: false
    },
    canAssignPortToTenant: {
      type: Boolean,
      default: false
    },
    canEditPorts: {
      type: Boolean,
      default: false
    },
    canDeletePorts: {
      type: Boolean,
      default: false
    },
    targetTenantId: {
      type: String,
      default: ''
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    const { t } = useI18n();
    const ACCORDION_EVENT = 'profile-left-card-open';
    const cardKey = 'ports';
    const isExpanded = ref(true);
    const onOtherCardOpened = (event) => {
      if (props.collapsible && event?.detail?.card !== cardKey) {
        isExpanded.value = false;
      }
    };

    const toggleCard = () => {
      if (!props.collapsible) return;
      if (isExpanded.value) {
        isExpanded.value = false;
        return;
      }
      window.dispatchEvent(new CustomEvent(ACCORDION_EVENT, { detail: { card: cardKey } }));
      isExpanded.value = true;
    };

    // Show the inline filter only once scanning the list actually becomes work
    const CARD_FILTER_THRESHOLD = 8;

    const ports = ref([]);
    const cardFilter = ref('');
    // Full (unpaginated) list of ports already assigned to the tenant, used by the
    // selection modal for pre-selection, auto-expansion, and sorting by countryName.
    const allAssignedPorts = ref([]);
    const isLoading = ref(false);
    const error = ref(null);
    const currentUser = computed(() => store.getters['auth/currentUser']);
    const deletingId = ref(null);
    const effectiveTenantId = computed(() => props.targetTenantId || currentUser.value?.tenantId || null);
    
    // Card list grouped by country so a supplier with many ports can scan by region
    // instead of reading a flat, unsorted list.
    const groupedCardPorts = computed(() => {
      const query = cardFilter.value.trim().toLowerCase();
      const groups = new Map();

      ports.value.forEach(port => {
        const name = port.portName || port.name || '';
        const code = port.portCode || port.code || '';
        if (query && !name.toLowerCase().includes(query) && !code.toLowerCase().includes(query)) {
          return;
        }
        const countryName = port.countryName || t('profile.ports.unknownCountry');
        if (!groups.has(countryName)) {
          groups.set(countryName, []);
        }
        groups.get(countryName).push(port);
      });

      return [...groups.entries()]
        .map(([countryName, list]) => ({
          countryName,
          ports: [...list].sort((a, b) =>
            (a.portName || a.name || '').localeCompare(b.portName || b.name || '')
          )
        }))
        .sort((a, b) => a.countryName.localeCompare(b.countryName));
    });

    // Countries the user opened in the card list. Separate from the modal's expandedCountries.
    const openCardCountries = ref(new Set());

    const isCardCountryOpen = (countryName) =>
      !!cardFilter.value.trim() || openCardCountries.value.has(countryName);

    const toggleCardCountry = (countryName) => {
      // While filtering every hit is shown open; toggling would do nothing visible.
      if (cardFilter.value.trim()) return;
      const next = new Set(openCardCountries.value);
      if (next.has(countryName)) {
        next.delete(countryName);
      } else {
        next.add(countryName);
      }
      openCardCountries.value = next;
    };

    // Pagination
    const currentPage = ref(0);
    // Grouping by country only reads well over a decent slice of the list, so the card
    // fetches a larger first page than the previous 5.
    const pageSize = ref(25);
    const totalItems = ref(0);
    const loadingMore = ref(false);
    
    // Computed property to determine if there are more pages to load
    const hasMorePages = computed(() => {
      return ports.value.length < totalItems.value;
    });
    
    // Port selection modal
    const selectedPortIds = ref([]);
    const searchQuery = ref('');
    const countryOptions = ref([]);
    const filteredCountryOptions = ref([]);
    const countryPorts = ref({}); 
    const loadingCountryPorts = ref({});
    const countryPortsLoaded = ref({}); 
    const expandedCountries = ref({}); 
    const isLoadingCountries = ref(false);
    const isSaving = ref(false);
    const showSelectedOnly = ref(false);
    const isSelectingAllCountries = ref(false);
    const allPortsLoaded = ref(false);
    const isLoadingAllPorts = ref(false);
    // Per-country open/closed state of the right-hand selection groups. Fully selected
    // countries default to collapsed so a large selection stays readable.
    const selectionGroupOpen = ref({});
    // Cache of id -> { id, name, countryName } for every port the user has selected,
    // so the summary panel can list them even when their country is collapsed/unloaded.
    const selectedPortDetails = ref({});
    let portSelectionModal = null;
    
    // Methods to manage ports by country
    const getPortsByCountry = (countryId) => {
      return countryPorts.value[countryId] || [];
    };
    
    const getFilteredPortsForCountry = (countryId) => {
      const ports = getPortsByCountry(countryId);
      if (!searchQuery.value.trim()) {
        return ports;
      }
      
      const query = searchQuery.value.toLowerCase().trim();
      return ports.filter(port => 
        (port.portName || port.name || '').toLowerCase().includes(query) || 
        (port.portCode || port.code || '').toLowerCase().includes(query)
      );
    };
    
    const getSelectedPortCountForCountry = (countryId) => {
      const ports = getPortsByCountry(countryId);
      return ports.filter(port =>
        selectedPortIds.value.includes(String(port.id))
      ).length;
    };

    const getCountrySelectedCount = (countryId) => {
      // If ports for this country have been loaded, count selections against that list
      if (countryPortsLoaded.value[countryId]) {
        return getSelectedPortCountForCountry(countryId);
      }

      // Otherwise fall back to counting already-assigned ports for this country by name,
      // using the full (unpaginated) assigned list so countries beyond page 1 count too
      const country = countryOptions.value.find(c => c.id === countryId);
      if (!country) {
        return 0;
      }
      const assignedPorts = allAssignedPorts.value.length ? allAssignedPorts.value : ports.value;
      return assignedPorts.filter(port =>
        port.countryName === country.name && selectedPortIds.value.includes(String(port.portID || port.id || port.portId))
      ).length;
    };

    const isCountryFullySelected = (countryId) => {
      const countryPortsList = getFilteredPortsForCountry(countryId);
      return countryPortsList.length > 0 &&
        countryPortsList.every(port => selectedPortIds.value.includes(String(port.id)));
    };

    // Partially selected countries render the checkbox in the indeterminate state,
    // so the user can tell "some" apart from "all" and "none" at a glance.
    const isCountryPartiallySelected = (countryId) => {
      const countryPortsList = getFilteredPortsForCountry(countryId);
      if (!countryPortsList.length) {
        return getCountrySelectedCount(countryId) > 0;
      }
      const selectedCount = countryPortsList.filter(
        port => selectedPortIds.value.includes(String(port.id))
      ).length;
      return selectedCount > 0 && selectedCount < countryPortsList.length;
    };

    const toggleSelectAllForCountry = async (countryId) => {
      // Make sure the country's ports are loaded before toggling, but keep the
      // panel closed if the user only clicked the checkbox.
      if (!countryPortsLoaded.value[countryId]) {
        await loadPortsForCountry(countryId);
      }

      const countryPortsList = getFilteredPortsForCountry(countryId);
      const countryPortIds = countryPortsList.map(port => String(port.id));

      if (isCountryFullySelected(countryId)) {
        selectedPortIds.value = selectedPortIds.value.filter(id => !countryPortIds.includes(id));
      } else {
        const idsToAdd = countryPortIds.filter(id => !selectedPortIds.value.includes(id));
        selectedPortIds.value = [...selectedPortIds.value, ...idsToAdd];
      }
    };

    const getPreSelectedCountForCountry = (countryName) => {
      // Count how many ports are already selected for this country, using the full
      // (unpaginated) assigned ports list so countries beyond page 1 sort correctly too
      return allAssignedPorts.value.filter(port =>
        port.countryName === countryName
      ).length;
    };
    
    const findPortDetailsById = (portId) => {
      for (const countryId in countryPorts.value) {
        const match = countryPorts.value[countryId].find(p => String(p.id) === String(portId));
        if (match) {
          const country = countryOptions.value.find(c => c.id === countryId || String(c.id) === String(countryId));
          return {
            id: String(match.id),
            name: match.portName || match.name,
            countryName: match.country || country?.name || t('profile.ports.unknownCountry')
          };
        }
      }

      const assigned = allAssignedPorts.value.find(
        p => String(p.portID || p.id || p.portId) === String(portId)
      );
      if (assigned) {
        return {
          id: String(portId),
          name: assigned.portName || assigned.name,
          countryName: assigned.countryName || t('profile.ports.unknownCountry')
        };
      }
      return null;
    };

    // Keep a stable name/country cache for every selected port so the summary panel
    // does not lose entries when a country's port list is unloaded or filtered out.
    watch(selectedPortIds, (ids) => {
      const cache = { ...selectedPortDetails.value };
      ids.forEach(id => {
        if (!cache[id]) {
          const details = findPortDetailsById(id);
          if (details) {
            cache[id] = details;
          }
        }
      });
      selectedPortDetails.value = cache;
    }, { deep: true });

    // Selected ports grouped by country, for the right-hand summary panel
    const selectedSummary = computed(() => {
      const groups = new Map();
      selectedPortIds.value.forEach(id => {
        const details = selectedPortDetails.value[id] || findPortDetailsById(id);
        if (!details) {
          return;
        }
        const key = details.countryName;
        if (!groups.has(key)) {
          groups.set(key, []);
        }
        groups.get(key).push(details);
      });

      return [...groups.entries()]
        .map(([countryName, portList]) => {
          const country = countryOptions.value.find(c => c.name === countryName);
          const totalPorts = country && countryPortsLoaded.value[country.id]
            ? getPortsByCountry(country.id).length
            : portList.length;
          return {
            countryName,
            countryId: country?.id || null,
            totalPorts,
            isFullySelected: totalPorts > 0 && portList.length >= totalPorts,
            ports: [...portList].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          };
        })
        .sort((a, b) => a.countryName.localeCompare(b.countryName));
    });

    // Countries shown in the left list; "Selected only" hides countries with no selection
    const visibleCountryOptions = computed(() => {
      if (!showSelectedOnly.value) {
        return filteredCountryOptions.value;
      }
      const selectedCountryNames = new Set(selectedSummary.value.map(group => group.countryName));
      return filteredCountryOptions.value.filter(country => selectedCountryNames.has(country.name));
    });

    // "Select all countries" is only fully checked once every listed country has been
    // loaded and has all of its ports selected.
    const areAllCountriesFullySelected = computed(() => {
      const countries = visibleCountryOptions.value;
      return countries.length > 0 && countries.every(country =>
        countryPortsLoaded.value[country.id] && isCountryFullySelected(country.id)
      );
    });

    const areSomeCountriesSelected = computed(() => selectedPortIds.value.length > 0);

    // Selecting every country means loading each country's ports first (they are lazy
    // loaded per country), so this runs in batches with a loading indicator.
    const toggleSelectAllCountries = async () => {
      const countries = [...visibleCountryOptions.value];

      if (areAllCountriesFullySelected.value) {
        const idsToRemove = new Set(
          countries.flatMap(country =>
            getFilteredPortsForCountry(country.id).map(port => String(port.id))
          )
        );
        selectedPortIds.value = selectedPortIds.value.filter(id => !idsToRemove.has(id));
        return;
      }

      isSelectingAllCountries.value = true;
      try {
        // One request covers every country
        await loadAllCountryPorts();

        const allIds = new Set(selectedPortIds.value);
        countries.forEach(country => {
          getFilteredPortsForCountry(country.id).forEach(port => allIds.add(String(port.id)));
        });
        selectedPortIds.value = [...allIds];
      } finally {
        isSelectingAllCountries.value = false;
      }
    };

    // Total number of ports across every country (header counter)
    const grandTotalPorts = computed(() => {
      if (allPortsLoaded.value) {
        return Object.values(countryPorts.value).reduce((sum, list) => sum + list.length, 0);
      }
      // Before the bulk load, fall back to the port counts the country list reports
      return countryOptions.value.reduce((sum, country) => sum + (country.portCount || 0), 0);
    });

    const anyCountryCollapsed = computed(() =>
      visibleCountryOptions.value.some(country => !expandedCountries.value[country.id])
    );

    const toggleExpandAll = () => {
      if (!anyCountryCollapsed.value) {
        expandedCountries.value = {};
        return;
      }
      const expanded = {};
      visibleCountryOptions.value.forEach(country => {
        expanded[country.id] = true;
      });
      expandedCountries.value = expanded;
    };

    const isSelectAllPartial = computed(() =>
      areSomeCountriesSelected.value && !areAllCountriesFullySelected.value
    );

    const selectAllGlyph = computed(() => {
      if (areAllCountriesFullySelected.value) return '✓';
      return isSelectAllPartial.value ? '–' : '';
    });

    const selectAllLabel = computed(() => {
      if (searchQuery.value.trim()) {
        return t('profile.ports.selectAllMatching');
      }
      return showSelectedOnly.value
        ? t('profile.ports.selectAllListed')
        : t('profile.ports.selectAllCountries');
    });

    const countryCheckboxGlyph = (countryId) => {
      if (isCountryFullySelected(countryId)) return '✓';
      return isCountryPartiallySelected(countryId) ? '–' : '';
    };

    const togglePortSelection = (portId) => {
      const id = String(portId);
      if (selectedPortIds.value.includes(id)) {
        selectedPortIds.value = selectedPortIds.value.filter(existing => existing !== id);
      } else {
        selectedPortIds.value = [...selectedPortIds.value, id];
      }
    };

    const clearCountrySelection = (countryId) => {
      const idsToRemove = new Set(getPortsByCountry(countryId).map(port => String(port.id)));
      selectedPortIds.value = selectedPortIds.value.filter(id => !idsToRemove.has(id));
    };

    // Fully selected countries collapse by default; an explicit click overrides that.
    const isSelectionGroupOpen = (group) => {
      const stored = selectionGroupOpen.value[group.countryName];
      return stored === undefined ? !group.isFullySelected : stored;
    };

    const toggleSelectionGroup = (countryName) => {
      const group = selectedSummary.value.find(g => g.countryName === countryName);
      if (!group) {
        return;
      }
      selectionGroupOpen.value = {
        ...selectionGroupOpen.value,
        [countryName]: !isSelectionGroupOpen(group)
      };
    };

    const removeSelectedCountry = (countryName) => {
      const group = selectedSummary.value.find(g => g.countryName === countryName);
      if (!group) {
        return;
      }
      const idsToRemove = new Set(group.ports.map(port => String(port.id)));
      selectedPortIds.value = selectedPortIds.value.filter(id => !idsToRemove.has(id));
    };

    let searchTimeout = null;
    const filterCountries = async () => {
      // Clear previous timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      
      // Debounce search to avoid too many API calls
      searchTimeout = setTimeout(async () => {
        isLoadingCountries.value = true;
        try {
          const response = await portService.getCountrySelectionItems(searchQuery.value.trim());
          if (response) {
            countryOptions.value = response;
            // Sort countries: ones with selected ports first
            const sorted = [...response].sort((a, b) => {
              const aHasSelected = getPreSelectedCountForCountry(a.name) > 0;
              const bHasSelected = getPreSelectedCountForCountry(b.name) > 0;
              
              if (aHasSelected && !bHasSelected) return -1;
              if (!aHasSelected && bHasSelected) return 1;
              return 0; // Keep original order for same selection status
            });
            filteredCountryOptions.value = sorted;
          }
        } catch (err) {
          console.error('Error searching countries:', err);
        } finally {
          isLoadingCountries.value = false;
        }
      }, 500); // 500ms debounce
    };
    
    const loadPorts = async () => {
      if (!effectiveTenantId.value) {
        console.warn('No tenantId found in user data');
        return;
      }
      
      isLoading.value = true;
      error.value = null;
      currentPage.value = 0;
      
      try {
        const response = await portService.searchPorts({
          page: currentPage.value,
          pageSize: pageSize.value,
          tenantId: effectiveTenantId.value
        });
        
        if (response) {
          ports.value = dedupeByPortId(response.items);
          totalItems.value = response.rowCount || response.totalCount || 0;
        }
      } catch (err) {
        console.error('Error loading ports:', err);
        error.value = t('profile.ports.loadError');
        store.dispatch('notify', {
          message: error.value,
          type: 'error'
        });
      } finally {
        isLoading.value = false;
      }
    };
    
    // Collapse items that map the same port. `id` on a PortVsTenantDto is the
    // AppPortVsTenants row id, so one port mapped twice arrives as two items with
    // different `id`s - deduping on `id` would keep both.
    const dedupeByPortId = (items) => {
      const seen = new Set();
      return (items || []).filter(item => {
        const portId = String(item.portID || item.portId || item.id || '');
        if (!portId || seen.has(portId)) {
          return false;
        }
        seen.add(portId);
        return true;
      });
    };

    const loadMorePorts = async () => {
      if (!effectiveTenantId.value || !hasMorePages.value) {
        return;
      }
      
      loadingMore.value = true;
      currentPage.value += 1;
      
      try {
        const response = await portService.searchPorts({
          page: currentPage.value,
          pageSize: pageSize.value,
          tenantId: effectiveTenantId.value
        });
        
        if (response && response.items) {
          // Dedupe the incoming page, then drop anything already on the card.
          const seenPortIds = new Set(
            ports.value.map(existingPort => String(existingPort.portID || existingPort.id))
          );
          const newPorts = dedupeByPortId(response.items).filter(
            newPort => !seenPortIds.has(String(newPort.portID || newPort.id))
          );

          ports.value = [...ports.value, ...newPorts];
          totalItems.value = response.rowCount || response.totalCount || 0;
        }
      } catch (err) {
        console.error('Error loading more ports:', err);
        store.dispatch('notify', {
          message: t('profile.ports.loadMoreError'),
          type: 'error'
        });
        // Revert page increment on error
        currentPage.value -= 1;
      } finally {
        loadingMore.value = false;
      }
    };
    
    // Update entity counts in store and emit event
    const updatePortsCount = async () => {
      try {
        // Get updated counts from API
        const response = await tenantInfoService.getEntityCounts(effectiveTenantId.value);
        if (response) {
          // Update store with new counts 
          store.commit('updateEntityCounts', response);
          
          // Emit event to notify parent components
          emit('ports-updated', {
            count: ports.value.length,
            totalSize: totalItems.value
          });
        }
      } catch (error) {
        console.error('Error updating port counts:', error);
      }
    };

    const deletePort = async (port) => {
      if (!port || !port.id) {
        console.error('Invalid port object');
        return;
      }
      
      try {
        deletingId.value = port.id;
        
        await portService.deletePort(port.id);
        
        // Reload ports from API to keep UI synced with backend state
        await loadPorts();
        
        // Update entity counts
        await updatePortsCount();
        
        store.dispatch('notify', {
          message: t('profile.ports.deleteSuccess'),
          type: 'success'
        });
      } catch (err) {
        console.error('Error deleting port:', err);
        store.dispatch('notify', {
          message: t('profile.ports.deleteError'),
          type: 'error'
        });
      } finally {
        deletingId.value = null;
      }
    };
    
    const loadCountryOptions = async (searchText = '') => {
      isLoadingCountries.value = true;
      try {
        const response = await portService.getCountrySelectionItems(searchText);
        if (response) {
          countryOptions.value = response;
          // Sort countries: ones with selected ports first
          const sorted = [...response].sort((a, b) => {
            const aHasSelected = getPreSelectedCountForCountry(a.name) > 0;
            const bHasSelected = getPreSelectedCountForCountry(b.name) > 0;
            
            if (aHasSelected && !bHasSelected) return -1;
            if (!aHasSelected && bHasSelected) return 1;
            return 0; // Keep original order for same selection status
          });
          filteredCountryOptions.value = sorted;
        }
      } catch (err) {
        console.error('Error loading country options:', err);
        store.dispatch('notify', {
          message: t('profile.ports.loadCountriesError'),
          type: 'error'
        });
      } finally {
        isLoadingCountries.value = false;
      }
    };
    
    // Loads every country's ports in a single request. The picker used to fetch ports
    // one country at a time, so expanding countries or selecting all of them fired
    // hundreds of requests; this fetches the whole set once per modal session.
    const loadAllCountryPorts = async () => {
      if (allPortsLoaded.value || isLoadingAllPorts.value) {
        return;
      }

      isLoadingAllPorts.value = true;
      try {
        const response = await portService.getAllPortSelectionItems();
        if (Array.isArray(response)) {
          const byCountry = {};
          const loaded = {};
          response.forEach(group => {
            if (!group?.countryId) {
              return;
            }
            byCountry[group.countryId] = (group.ports || []).map(port => ({
              id: port.id,
              portName: port.name,
              portCode: port.portCode,
              country: group.countryName
            }));
            loaded[group.countryId] = true;
          });
          countryPorts.value = byCountry;
          countryPortsLoaded.value = loaded;
          allPortsLoaded.value = true;
        }
      } catch (err) {
        console.error('Error loading all ports:', err);
        store.dispatch('notify', {
          message: t('profile.ports.loadError'),
          type: 'error'
        });
      } finally {
        isLoadingAllPorts.value = false;
      }
    };

    // Kept for the case where the bulk load failed: falls back to a single-country fetch.
    const loadPortsForCountry = async (countryId) => {
      if (countryPortsLoaded.value[countryId] || loadingCountryPorts.value[countryId]) {
        return;
      }

      loadingCountryPorts.value[countryId] = true;

      try {
        const response = await portService.getPortSelectionItems(countryId);
        if (response) {
          countryPorts.value[countryId] = response;
          countryPortsLoaded.value[countryId] = true;
        }
      } catch (err) {
        console.error('Error loading ports for country:', err);
        store.dispatch('notify', {
          message: t('profile.ports.loadError'),
          type: 'error'
        });
      } finally {
        loadingCountryPorts.value[countryId] = false;
      }
    };
    
    const toggleCountryAccordion = async (countryId) => {
      // Toggle expanded state
      expandedCountries.value[countryId] = !expandedCountries.value[countryId];
      
      // Load ports if expanding and not already loaded
      if (expandedCountries.value[countryId] && !countryPortsLoaded.value[countryId]) {
        // Normally the bulk load already covers this; only needed if it failed
        await loadPortsForCountry(countryId);
      }
    };

    const collapseAllCountries = () => {
      expandedCountries.value = {};
    };

    const clearSearch = () => {
      searchQuery.value = '';
      filterCountries();
    };

    const enableShowSelectedOnly = () => {
      showSelectedOnly.value = true;
      // Expand every country that has a selection so the user immediately sees what is picked
      selectedSummary.value.forEach(group => {
        const country = countryOptions.value.find(c => c.name === group.countryName);
        if (country) {
          expandedCountries.value[country.id] = true;
        }
      });
    };

    const removeSelectedPort = (portId) => {
      selectedPortIds.value = selectedPortIds.value.filter(id => id !== String(portId));
    };

    const clearAllSelections = () => {
      selectedPortIds.value = [];
    };

    const resetModalState = () => {
      searchQuery.value = '';
      countryPorts.value = {};
      loadingCountryPorts.value = {};
      countryPortsLoaded.value = {};
      expandedCountries.value = {};
      allAssignedPorts.value = [];
      showSelectedOnly.value = false;
      selectedPortDetails.value = {};
      allPortsLoaded.value = false;
      selectionGroupOpen.value = {};
    };
    
    const preSelectExistingPorts = () => {
      const currentPortIds = ports.value.map(port => {
        return String(port.portID || port.id || port.portId);
      }).filter(Boolean);
      selectedPortIds.value = [...currentPortIds];
    };

    // Loads the full list of ports already assigned to the tenant (no pagination),
    // so the selection modal can correctly pre-select all of them, not just the first page.
    const loadAllExistingPorts = async () => {
      if (!effectiveTenantId.value) {
        return [];
      }

      try {
        const response = await portService.searchPorts({
          page: 0,
          pageSize: totalItems.value > pageSize.value ? totalItems.value : pageSize.value,
          tenantId: effectiveTenantId.value
        });
        return dedupeByPortId(response?.items);
      } catch (err) {
        console.error('Error loading all existing ports:', err);
        return ports.value;
      }
    };
    
    // The bulk load already holds every country's ports, so counts and the summary
    // panel are accurate without extra requests. Countries stay collapsed on open —
    // the summary panel on the right shows what is selected.
    const loadAndExpandCountries = async () => {
      await loadAllCountryPorts();
    };
    
    const openPortModal = async () => {
      resetModalState();
      await loadPorts();

      // loadPorts() only fetches the first page; fetch the full assigned list
      // so pre-selection and auto-expansion in the modal cover every existing
      // port, not just page 1.
      let allExistingPorts = ports.value;
      if (totalItems.value > ports.value.length) {
        allExistingPorts = await loadAllExistingPorts();
        selectedPortIds.value = allExistingPorts.map(port => String(port.portID || port.id || port.portId)).filter(Boolean);
      } else {
        preSelectExistingPorts();
      }
      allAssignedPorts.value = allExistingPorts;

      await loadCountryOptions();

      await loadAndExpandCountries();
      
      if (portSelectionModal) {
        portSelectionModal.show();
      }
    };
    
    const closeModal = () => {
      if (portSelectionModal) {
        portSelectionModal.hide();
      }
    };
    
    const savePorts = async () => {
      if (!effectiveTenantId.value) {
        return;
      }
      
      isSaving.value = true;
      
      try {
        // Only the IDs are sent, so there is no need to resolve each port object here.
        // replaceExisting lets the API remove the mappings the user deselected.
        await portService.savePorts(effectiveTenantId.value, [...selectedPortIds.value]);

        // Reload the ports from the API to get the correct data
        await loadPorts();

        // Update entity counts
        await updatePortsCount();

        // Show success notification
        store.dispatch('notify', {
          message: t('profile.ports.saveSuccess'),
          type: 'success'
        });
        
        // Close the modal
        if (portSelectionModal) {
          portSelectionModal.hide();
        }
      } catch (err) {
        console.error('Error saving ports:', err);
        store.dispatch('notify', {
          message: t('profile.ports.saveError'),
          type: 'error'
        });
      } finally {
        isSaving.value = false;
      }
    };
    
    onMounted(() => {
      window.addEventListener(ACCORDION_EVENT, onOtherCardOpened);
      loadPorts();
      
      // Initialize Bootstrap modal
      if (typeof bootstrap !== 'undefined') {
        const modalElement = document.getElementById('portSelectionModal');
        if (modalElement) {
          portSelectionModal = new bootstrap.Modal(modalElement);
          
        }
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener(ACCORDION_EVENT, onOtherCardOpened);
    });
    
    return {
      ports,
      isLoading,
      error,
      deletingId,
      deletePort,
      openPortModal,
      closeModal,
      selectedPortIds,
      searchQuery,
      countryOptions,
      filteredCountryOptions,
      countryPorts,
      loadingCountryPorts,
      countryPortsLoaded,
      expandedCountries,
      isLoadingCountries,
      filterCountries,
      toggleCountryAccordion,
      getPortsByCountry,
      getFilteredPortsForCountry,
      getSelectedPortCountForCountry,
      getPreSelectedCountForCountry,
      getCountrySelectedCount,
      isCountryFullySelected,
      isCountryPartiallySelected,
      toggleSelectAllForCountry,
      showSelectedOnly,
      enableShowSelectedOnly,
      collapseAllCountries,
      clearSearch,
      visibleCountryOptions,
      areAllCountriesFullySelected,
      areSomeCountriesSelected,
      toggleSelectAllCountries,
      isSelectingAllCountries,
      isLoadingAllPorts,
      grandTotalPorts,
      anyCountryCollapsed,
      toggleExpandAll,
      isSelectAllPartial,
      selectAllGlyph,
      selectAllLabel,
      countryCheckboxGlyph,
      togglePortSelection,
      clearCountrySelection,
      isSelectionGroupOpen,
      toggleSelectionGroup,
      removeSelectedCountry,
      selectedSummary,
      removeSelectedPort,
      clearAllSelections,
      isSaving,
      savePorts,
      // Pagination
      hasMorePages,
      loadMorePorts,
      loadingMore,
      totalItems,
      currentPage,
      isExpanded,
      toggleCard,
      CARD_FILTER_THRESHOLD,
      cardFilter,
      groupedCardPorts,
      isCardCountryOpen,
      toggleCardCountry,
      t
    };
  }
};
</script>

<style scoped>
/* ============================================================
   Ports card — VESPROC card shell (.vp-card) with a collapsible head
   ============================================================ */
/* Modal-only: the host screen draws the list, so the card contributes no box of its own. */
.port-card-modal-only {
  display: contents;
}

.pv-accordion-head {
  cursor: pointer;
  user-select: none;
}

.pv-accordion-head:focus-visible {
  outline: 0;
  box-shadow: inset 0 0 0 2px rgba(var(--vp-primary-rgb), 0.25);
}

.pv-accordion-caret {
  font-size: 0.95rem;
  color: var(--vp-gray-500);
}

/* Inline filter */
.port-card-filter {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.port-card-filter-icon {
  position: absolute;
  left: 10px;
  font-size: 0.9rem;
  color: var(--vp-gray-500);
  pointer-events: none;
}

.port-card-filter input {
  width: 100%;
  font: inherit;
  font-size: 0.8125rem;
  border: 1px solid var(--vp-gray-300);
  border-radius: var(--vp-radius);
  padding: 6px 30px;
  color: var(--vp-body-color);
  background: var(--vp-surface);
  outline: none;
}

.port-card-filter input:focus {
  border-color: var(--vp-primary);
  box-shadow: 0 0 0 3px rgba(var(--vp-primary-rgb), 0.12);
}

.port-card-filter-clear {
  position: absolute;
  right: 6px;
  border: 0;
  background: none;
  color: var(--vp-gray-500);
  cursor: pointer;
  font-size: 0.8rem;
  padding: 2px;
  line-height: 1;
}

.port-card-filter-clear:hover {
  color: var(--vp-danger);
}

/* Country list — one row per country, ports open underneath on demand */
.port-card-countries {
  border: 1px solid var(--vp-gray-300);
  border-radius: var(--vp-radius);
  overflow: hidden;
}

.port-card-group + .port-card-group {
  border-top: 1px solid var(--vp-gray-200);
}

.port-card-group-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border: 0;
  background: var(--vp-surface);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.port-card-group-header:hover {
  background: var(--vp-gray-100);
}

.port-card-group-header:focus-visible {
  outline: 0;
  box-shadow: inset 0 0 0 2px rgba(var(--vp-primary-rgb), 0.25);
}

.port-card-group.is-open .port-card-group-header {
  background: var(--vp-gray-100);
}

.port-card-group-caret {
  font-size: 0.8rem;
  color: var(--vp-gray-500);
  transition: transform 0.15s ease;
}

.port-card-group.is-open .port-card-group-caret {
  transform: rotate(90deg);
}

.port-card-group-icon {
  font-size: 1rem;
  color: var(--vp-primary);
}

.port-card-group-name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--vp-heading-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.port-card-group-count {
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--vp-gray-600);
  background: var(--vp-gray-200);
  border-radius: var(--vp-radius-chip);
  padding: 1px 8px;
}

.port-card-group-body {
  padding: 4px 8px 8px 32px;
  border-top: 1px solid var(--vp-gray-200);
}

/* One port = one compact row */
.port-card-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  border-radius: var(--vp-radius-chip);
  transition: background-color 0.15s ease;
}

.port-card-row:hover {
  background-color: var(--vp-gray-100);
}

.port-card-row-icon {
  font-size: 0.9rem;
  color: var(--vp-primary);
  flex: none;
}

.port-card-row-name {
  font-size: 0.8125rem;
  color: var(--vp-heading-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.port-card-row-code {
  font-size: 0.68rem;
  color: var(--vp-gray-600);
  background: var(--vp-gray-100);
  border: 1px solid var(--vp-gray-200);
  border-radius: 4px;
  padding: 0 5px;
  flex: none;
}

.port-card-row-delete {
  margin-left: auto;
  border: 0;
  background: none;
  color: var(--vp-gray-400);
  cursor: pointer;
  padding: 2px 4px;
  font-size: 0.85rem;
  line-height: 1;
  flex: none;
  opacity: 0;
  transition: opacity 0.15s ease, color 0.15s ease;
}

/* Reveal delete on hover/focus so the row stays quiet at rest,
   but keep it always visible on touch devices where hover never fires. */
.port-card-row:hover .port-card-row-delete,
.port-card-row-delete:focus-visible,
.port-card-row-delete:disabled {
  opacity: 1;
}

.port-card-row-delete:hover:not(:disabled) {
  color: var(--vp-danger);
}

@media (hover: none) {
  .port-card-row-delete {
    opacity: 1;
  }
}

/* ============================================================
   Port selection modal — follows the VESPROC design tokens
   (primary #00a0df, body #39465f, headings #1d2630, muted #8996a4)
   ============================================================ */
.port-modal-dialog {
  max-width: min(1180px, 96vw);
  height: min(840px, 94vh);
  margin: auto;
}

.port-modal-content {
  height: 100%;
  border: 0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(27, 46, 94, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #39465f;
  font-size: 0.875rem;
}

.port-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #f3f5f7;
  flex: none;
}

.port-modal-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1d2630;
}

.port-modal-subtitle,
.port-select-all-hint,
.port-country-total {
  font-size: 0.75rem;
  color: #8996a4;
}

.port-modal-body {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: 1fr 372px;
  min-height: 0;
}

/* ---------- left: browse ---------- */
.port-browse {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.port-browse-toolbar {
  padding: 14px 20px 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: none;
}

.port-search {
  position: relative;
}

.port-search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: #8996a4;
  font-size: 0.8125rem;
}

.port-search-input {
  width: 100%;
  box-sizing: border-box;
  font: inherit;
  font-size: 0.8125rem;
  border: 1px solid #dbe0e5;
  border-radius: 8px;
  padding: 7px 30px;
  color: #39465f;
  outline: none;
}

.port-search-input:focus {
  border-color: #00a0df;
}

.port-search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: none;
  color: #8996a4;
  cursor: pointer;
  font-size: 0.8125rem;
  padding: 2px;
}

/* Segmented "All countries / Selected only" control */
.port-segmented {
  display: flex;
  background: #f3f5f7;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}

.port-segment {
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  border: 0;
  border-radius: 8px;
  padding: 5px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  color: #5b6b79;
}

.port-segment.is-active {
  background: #39465f;
  color: #fff;
}

.port-segment-badge {
  background: rgba(0, 160, 223, 0.15);
  color: #0088be;
  border-radius: 50rem;
  font-size: 0.68rem;
  padding: 1px 7px;
}

.port-link-btn {
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  border: 0;
  background: none;
  color: #00a0df;
  cursor: pointer;
  padding: 4px 6px;
}

/* Select-all row */
.port-select-all {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8f9fa;
  border: 1px solid #f3f5f7;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
}

.port-select-all:hover {
  background: #f3f5f7;
}

.port-select-all-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1d2630;
}

/* Custom checkbox */
.port-cb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  font-weight: 700;
  color: #fff;
  background: #fff;
  border: 1.5px solid #bec8d0;
  cursor: pointer;
  user-select: none;
}

.port-cb-lg {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  font-size: 12px;
}

.port-cb-sm {
  width: 15px;
  height: 15px;
  border-radius: 4px;
  font-size: 10px;
}

.port-cb.is-checked,
.port-cb.is-partial {
  background: #00a0df;
  border-color: #00a0df;
}

/* Country list */
.port-browse-list {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 20px 16px;
  min-height: 0;
}

.port-country {
  border: 1px solid #f3f5f7;
  border-radius: 8px;
  overflow: hidden;
}

.port-country-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #fff;
}

.port-country-header:hover {
  background: #f8f9fa;
}

.port-country-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  background: none;
  padding: 0;
  cursor: pointer;
  text-align: start;
  color: inherit;
}

.port-chevron {
  font-size: 0.7rem;
  color: #8996a4;
  display: inline-block;
  transition: transform 0.15s;
}

.port-chevron.is-open {
  transform: rotate(90deg);
}

.port-chevron-sm {
  font-size: 0.62rem;
}

.port-country-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1d2630;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.port-count-badge {
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 50rem;
  padding: 1px 8px;
  background: #f3f5f7;
  color: #8996a4;
}

.port-count-badge.is-filled {
  background: #00a0df;
  color: #fff;
}

.port-clear-btn {
  font: inherit;
  font-size: 0.7rem;
  font-weight: 600;
  border: 0;
  background: none;
  color: #8996a4;
  cursor: pointer;
  padding: 2px 4px;
}

.port-clear-btn:hover {
  color: #f44236;
}

.port-country-body {
  border-top: 1px solid #f3f5f7;
  background: #fbfcfd;
  padding: 10px 12px 10px 38px;
}

/* Ports render in a responsive grid so a large country stays compact */
.port-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
  gap: 4px 12px;
}

.port-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 6px;
  cursor: pointer;
  min-width: 0;
}

.port-cell:hover {
  background: rgba(0, 160, 223, 0.06);
}

.port-cell-name {
  font-size: 0.78rem;
  color: #39465f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.port-cell-code {
  color: #8996a4;
}

/* ---------- right: selection panel ---------- */
.port-panel {
  border-left: 1px solid #f3f5f7;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.port-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px 10px;
  flex: none;
}

.port-panel-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1d2630;
}

.port-panel-count {
  background: #00a0df;
  color: #fff;
  border-radius: 50rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 1px 8px;
}

.port-danger-btn {
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  border: 0;
  background: none;
  color: #f44236;
  cursor: pointer;
  padding: 2px 4px;
}

.port-panel-summary {
  padding: 0 18px 10px;
  flex: none;
  font-size: 0.72rem;
  color: #8996a4;
}

.port-panel-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 18px 16px;
  min-height: 0;
}

.port-group {
  background: #fff;
  border: 1px solid #f3f5f7;
  border-radius: 8px;
  overflow: hidden;
}

.port-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  cursor: pointer;
}

.port-group-header:hover {
  background: #f8f9fa;
}

.port-group-name {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #39465f;
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.port-group-badge {
  font-size: 0.68rem;
  font-weight: 600;
  border-radius: 50rem;
  padding: 1px 8px;
  background: rgba(0, 160, 223, 0.15);
  color: #0088be;
}

.port-group-badge.is-filled {
  background: #00a0df;
  color: #fff;
}

.port-group-body {
  border-top: 1px solid #f3f5f7;
  padding: 8px 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.port-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  background: rgba(0, 160, 223, 0.12);
  color: #0088be;
  border-radius: 6px;
  padding: 3px 7px 3px 9px;
  font-size: 0.72rem;
  font-weight: 600;
}

.port-chip-remove {
  border: 0;
  background: none;
  color: #0088be;
  cursor: pointer;
  font-size: 0.7rem;
  padding: 0;
  line-height: 1;
}

.port-chip-remove:hover {
  color: #f44236;
}

/* ---------- shared ---------- */
.port-icon-btn {
  border: 0;
  background: none;
  font-size: 1rem;
  color: #8996a4;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}

.port-icon-btn:hover {
  color: #f44236;
}

.port-icon-btn-sm {
  font-size: 0.75rem;
  padding: 0 2px;
}

.port-state-block {
  text-align: center;
  padding: 40px 16px;
}

.port-state-text {
  color: #8996a4;
  font-size: 0.8125rem;
}

/* ---------- footer ---------- */
.port-modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #f3f5f7;
  flex: none;
}

.port-footer-text {
  font-size: 0.75rem;
  color: #5b6b79;
}

.port-btn {
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: 10px;
  padding: 6px 16px;
  cursor: pointer;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
}

.port-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.port-btn-secondary {
  background: #f8f9fa;
  border-color: #dbe0e5;
  color: #39465f;
}

.port-btn-primary {
  background: #00a0df;
  color: #fff;
  padding: 6px 18px;
}

.port-btn-primary:hover:not(:disabled) {
  background: #0088be;
}

@media (max-width: 991.98px) {
  .port-modal-dialog {
    height: auto;
  }

  .port-modal-body {
    grid-template-columns: 1fr;
  }

  .port-panel {
    border-left: 0;
    border-top: 1px solid #f3f5f7;
  }

  .port-browse-list,
  .port-panel-body {
    max-height: 45vh;
  }
}
</style>